import {
  JwkSet,
  JwkSetResponse,
  LtiPlatform,
  LtiPlatformResponse,
  LtiPlatformsListResponse,
  LtiResourceLink,
  LtiResourceLinkList,
  LtiTenantStatus,
  LtiResourceLinkResponse,
  LtiDeepLinkingCompletion,
  LtiDeepLinkingCompletionResponse,
  LtiSelectableResource,
  LtiUnmappedLink,
  LtiUnmappedLinksResponse,
  MapResourceLinkRequest,
  RegisterLtiPlatformRequest,
} from '@/types/lti.types';
import { API_ENDPOINTS } from '../config/api.config';
import apiClient from './api';

/**
 * Admin API for the LTI 1.3 Integrations feature.
 * All calls hit /lti/admin/* and unwrap the TypedResultEntity envelope
 * (response.data.data), matching the rest of the service layer.
 */
class LtiService {
  /** List all LMS platforms registered for this tenant. */
  async getPlatforms(): Promise<LtiPlatform[]> {
    const response = await apiClient.get<LtiPlatformsListResponse>(
      API_ENDPOINTS.LTI.PLATFORMS,
    );
    return response.data.data;
  }

  /** Get a single registered platform. */
  async getPlatform(id: string): Promise<LtiPlatform> {
    const response = await apiClient.get<LtiPlatformResponse>(
      API_ENDPOINTS.LTI.PLATFORM_BY_ID(id),
    );
    return response.data.data;
  }

  /** Register a new LMS platform. */
  async createPlatform(data: RegisterLtiPlatformRequest): Promise<LtiPlatform> {
    const response = await apiClient.post<LtiPlatformResponse>(
      API_ENDPOINTS.LTI.PLATFORMS,
      data,
    );
    return response.data.data;
  }

  /** Update a platform's mutable fields. */
  async updatePlatform(
    id: string,
    data: Partial<RegisterLtiPlatformRequest>,
  ): Promise<LtiPlatform> {
    const response = await apiClient.put<LtiPlatformResponse>(
      API_ENDPOINTS.LTI.PLATFORM_BY_ID(id),
      data,
    );
    return response.data.data;
  }

  /** Suspend a platform - retained but launches are rejected. */
  async suspendPlatform(id: string): Promise<LtiPlatform> {
    const response = await apiClient.patch<LtiPlatformResponse>(
      API_ENDPOINTS.LTI.PLATFORM_SUSPEND(id),
    );
    return response.data.data;
  }

  /** Re-activate a suspended platform; its launches are accepted again. */
  async activatePlatform(id: string): Promise<LtiPlatform> {
    const response = await apiClient.patch<LtiPlatformResponse>(
      API_ENDPOINTS.LTI.PLATFORM_ACTIVATE(id),
    );
    return response.data.data;
  }

  /** Whether LTI is switched on for this tenant. */
  async getLtiConfig(): Promise<LtiTenantStatus> {
    const response = await apiClient.get(API_ENDPOINTS.LTI.CONFIG);
    return response.data?.data ?? response.data;
  }

  /** Turn LTI on or off. Enabling generates the tenant's master key if missing. */
  async setLtiEnabled(enabled: boolean): Promise<LtiTenantStatus> {
    const response = await apiClient.put(API_ENDPOINTS.LTI.CONFIG, { enabled });
    return response.data?.data ?? response.data;
  }

  /** Every mapped resource link — what each LMS activity opens. */
  async listResourceLinks(params: {
    issuer?: string;
    skip?: number;
    limit?: number;
  } = {}): Promise<LtiResourceLinkList> {
    const response = await apiClient.get(API_ENDPOINTS.LTI.RESOURCE_LINKS, { params });
    const data = response.data?.data ?? response.data;
    return { items: data?.items ?? [], total: data?.total ?? 0 };
  }

  /** Forget a mapping — for an activity that no longer exists in the LMS. */
  async deleteResourceLink(issuer: string, resourceLinkId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.LTI.RESOURCE_LINK_BY_ID(issuer, resourceLinkId));
  }

  /** Map an LMS resource link to a Skillzen course/certification/learning-path. */
  async mapResourceLink(
    data: MapResourceLinkRequest,
  ): Promise<LtiResourceLink> {
    const response = await apiClient.post<LtiResourceLinkResponse>(
      API_ENDPOINTS.LTI.RESOURCE_LINKS,
      data,
    );
    return response.data.data;
  }

  /** Read this tenant's published JWKS (to copy into the LMS tool config). */
  async getJwks(): Promise<JwkSet> {
    const response = await apiClient.get<JwkSetResponse>(
      API_ENDPOINTS.LTI.JWKS,
    );
    return response.data.data;
  }
  /**
   * Send the teacher's content selection to be signed.
   *
   * Returns the LMS URL to form-post to and the signed JWT. The session id
   * proves which LMS request this answers; the return URL is never sent from
   * here, it comes from the server-side session.
   */
  async completeDeepLinking(
    sessionId: string,
    resources: LtiSelectableResource[]
  ): Promise<LtiDeepLinkingCompletion> {
    const response = await apiClient.post<LtiDeepLinkingCompletionResponse>(
      API_ENDPOINTS.LTI.DEEP_LINKING_COMPLETE,
      { session_id: sessionId, resources }
    );
    return response.data.data;
  }
  /**
   * Links an LMS has launched that are not mapped to Skillzen content yet.
   * Each carries the LMS course and activity titles, so an administrator can
   * identify it without knowing the LMS's internal id.
   */
  async getUnmappedLinks(): Promise<LtiUnmappedLink[]> {
    const response = await apiClient.get<LtiUnmappedLinksResponse>(
      API_ENDPOINTS.LTI.UNMAPPED_LINKS,
    );
    return response.data.data;
  }
}

export const ltiService = new LtiService();
export default ltiService;
